// These helpers are calling this template's own server-side routes
// so, they are not directly calling Marketplace API or Integration API.
// You can find these api endpoints from 'server/api/...' directory

import appSettings from '../config/settings';
import { types as sdkTypes, transit } from './sdkLoader';
import Decimal from 'decimal.js';

export const apiBaseUrl = marketplaceRootURL => {
  const port = process.env.REACT_APP_DEV_API_SERVER_PORT;
  const useDevApiServer = process.env.NODE_ENV === 'development' && !!port;

  // In development, the dev API server is running in a different port
  if (useDevApiServer) {
    return `http://localhost:${port}`;
  }

  // Otherwise, use the given marketplaceRootURL parameter or the same domain and port as the frontend
  return marketplaceRootURL ? marketplaceRootURL.replace(/\/$/, '') : `${window.location.origin}`;
};

// Application type handlers for JS SDK.
//
// NOTE: keep in sync with `typeHandlers` in `server/api-util/sdk.js`
export const typeHandlers = [
  // Use Decimal type instead of SDK's BigDecimal.
  {
    type: sdkTypes.BigDecimal,
    customType: Decimal,
    writer: v => new sdkTypes.BigDecimal(v.toString()),
    reader: v => new Decimal(v.value),
  },
];

const serialize = data => {
  return transit.write(data, { typeHandlers, verbose: appSettings.sdk.transitVerbose });
};

const deserialize = str => {
  return transit.read(str, { typeHandlers });
};

const methods = {
  POST: 'POST',
  GET: 'GET',
  PUT: 'PUT',
  PATCH: 'PATCH',
  DELETE: 'DELETE',
};

// If server/api returns data from SDK, you should set Content-Type to 'application/transit+json'
const request = (path, options = {}) => {
  const url = `${apiBaseUrl()}${path}`;
  const { credentials, headers, body, ...rest } = options;

  // If headers are not set, we assume that the body should be serialized as transit format.
  const contentType = headers?.['Content-Type'];
  const shouldSerializeTransitBody =
    (!headers || contentType === 'application/transit+json') && body;
  const shouldSerializeJsonBody = contentType === 'application/json' && body;
  const bodyMaybe = shouldSerializeTransitBody
    ? { body: serialize(body) }
    : shouldSerializeJsonBody
    ? { body: JSON.stringify(body) }
    : body
    ? { body }
    : {};

  // In cross-origin iframes, browsers block third-party cookies. When the SDK
  // auth token is stored in memory (iframe mode), pass it via a custom header
  // so the server can read it instead of relying on cookies.
  const iframeToken =
    typeof window !== 'undefined' && window.__iframeTokenStore
      ? window.__iframeTokenStore.getToken()
      : null;
  const iframeAuthHeader = iframeToken?.access_token
    ? { 'X-Iframe-Token': JSON.stringify(iframeToken) }
    : {};

  const fetchOptions = {
    credentials: credentials || 'include',
    // Since server/api mostly talks to Marketplace API using SDK,
    // we default to 'application/transit+json' as content type (as SDK uses transit).
    headers: {
      ...(headers || { 'Content-Type': 'application/transit+json' }),
      ...iframeAuthHeader,
    },
    ...bodyMaybe,
    ...rest,
  };

  return window.fetch(url, fetchOptions).then(res => {
    const contentTypeHeader = res.headers.get('Content-Type');
    const contentType = contentTypeHeader ? contentTypeHeader.split(';')[0] : null;

    if (res.status >= 400) {
      return res.json().then(data => {
        let e = new Error();
        e = Object.assign(e, data);

        throw e;
      });
    }
    if (contentType === 'application/transit+json') {
      return res.text().then(deserialize);
    } else if (contentType === 'application/json') {
      return res.json();
    }
    return res.text();
  });
};

// Keep the previous parameter order for the post method.
// For now, only POST has own specific function, but you can create more or use request directly.
const post = (path, body, options = {}) => {
  const requestOptions = {
    ...options,
    method: methods.POST,
    body,
  };
  return request(path, requestOptions);
};

// Ask for an approved store that owns the same business domain as an unapproved account.
//
// Only meaningful once `/store/:id` has already failed its approval check: the explicit id in the
// URL wins whenever it is usable. Resolves to `{ dealerId: null, reason }` when no substitute
// applies, so callers can fall through to their existing not-found handling.
//
// See `server/api/users.js` (`resolveApprovedStoreFallback`).
export const resolveApprovedStoreFallback = body => {
  return post('/api/dealers/resolve-store-fallback', body, {
    headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
  });
};

// Fetch transaction line items from the local API endpoint.
//
// See `server/api/transaction-line-items.js` to see what data should
// be sent in the body.
export const transactionLineItems = body => {
  return post('/api/transaction-line-items', body);
};

// Fetch a signed JWT that authenticates the current user in the Intercom Messenger.
//
// Resolves to null whenever there is no authenticated session or Messenger
// security is not configured, so the caller can fall back to an anonymous boot.
//
// See `server/api/intercom-jwt.js`.
export const fetchIntercomJwt = () => {
  return post('/api/intercom/jwt', null, {
    headers: { 'Content-Type': 'application/json' },
  })
    .then(response => response?.jwt || null)
    .catch(() => null);
};

export const fetchLightspeedConfig = () =>
  request('/api/lightspeed/config', {
    method: methods.GET,
    headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
  });

export const disconnectLightspeed = () =>
  post('/api/lightspeed/oauth/disconnect', null, {
    headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
  });

export const fetchLightspeedSyncStatus = () =>
  request('/api/lightspeed/sync-status', {
    method: methods.GET,
    headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
  });

export const fetchLightspeedActivity = (limit = 20) =>
  request(`/api/lightspeed/activity?limit=${encodeURIComponent(limit)}`, {
    method: methods.GET,
    headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
  });

export const fetchLightspeedOrderIncidents = () =>
  request('/api/lightspeed/order-incidents', {
    method: methods.GET,
    headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
  });

export const recheckLightspeedOrderIncident = transactionId =>
  post(`/api/lightspeed/order-incidents/${encodeURIComponent(transactionId)}/recheck`, null, {
    headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
  });

export const approveLightspeedOrderCorrection = (transactionId, originalSaleId) =>
  post(
    `/api/lightspeed/order-incidents/${encodeURIComponent(transactionId)}/approve-correction`,
    { confirm: true, originalSaleId },
    { headers: { 'Content-Type': 'application/json', Accept: 'application/json' } }
  );

export const saveLightspeedConfig = body =>
  post('/api/lightspeed/config', body, {
    headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
  });

export const runLightspeedDryRun = body =>
  post('/api/lightspeed/dry-run', body, {
    headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
  });

// Initiate a privileged transaction.
//
// With privileged transitions, the transactions need to be created
// from the backend. This endpoint enables sending the order data to
// the local backend, and passing that to the Marketplace API.
//
// See `server/api/initiate-privileged.js` to see what data should be
// sent in the body.
export const initiatePrivileged = body => {
  return post('/api/initiate-privileged', body);
};

// Transition a transaction with a privileged transition.
//
// This is similar to the `initiatePrivileged` above. It will use the
// backend for the transition. The backend endpoint will add the
// payment line items to the transition params.
//
// See `server/api/transition-privileged.js` to see what data should
// be sent in the body.
export const transitionPrivileged = body => {
  return post('/api/transition-privileged', body);
};

// Lightweight confirm-payment transition that skips line-item recalculation.
// Used by cart checkout in iframes where cookies are blocked.
export const confirmPayment = body => {
  return post('/api/confirm-payment', body);
};

// Send the custom guest receipt only after the paid marketplace transition succeeds.
export const sendGuestPurchaseConfirmation = body => {
  return post('/api/guest-purchase-confirmation', body);
};

// Create user with identity provider (e.g. Facebook or Google)
//
// If loginWithIdp api call fails and user can't authenticate to Marketplace API with idp
// we will show option to create a new user with idp.
// For that user needs to confirm data fetched from the idp.
// After the confirmation, this endpoint is called to create a new user with confirmed data.
//
// See `server/api/auth/createUserWithIdp.js` to see what data should
// be sent in the body.
export const createUserWithIdp = body => {
  return post('/api/auth/create-user-with-idp', body);
};

// Turnstile-protected login (calls server proxy instead of SDK directly)
export const loginWithTurnstile = body => {
  return post('/api/auth/login', body, {
    headers: { 'Content-Type': 'application/json' },
  });
};

// Turnstile-protected signup (calls server proxy instead of SDK directly)
export const signupWithTurnstile = body => {
  return post('/api/auth/signup', body, {
    headers: { 'Content-Type': 'application/json' },
  });
};

// Validate Shippo address
export const validateShippoAddress = body => {
  return post('/api/validate-shippo-address', body);
};

// Get Shippo rates
export const shippoGetRates = body => {
  return post('/api/shippo-get-rates', body);
};

// Get Shippo rates for a shipment id
export const shippoGetRatesFromId = body => {
  return request('/api/shippo-get-rates-from-id', body);
};

// Create shippo label
export const shippoCreateLabel = body => {
  return post('/api/shippo-create-label', body);
};

export const getUserProfile = body => {
  return post('/api/getUserData', body);
};

export const updateInvitation = body => {
  return post('/api/invitations/set', body);
};

export const getGroups = body => {
  return post('/api/invitations/list', body);
};

export const handleGroup = body => {
  return post('/api/invitations/action', body);
};

export const updateOwnerProfile = (actionPayload, queryParams) => {
  let body = { actionPayload, queryParams };
  return post('/api/invitations/handle', body);
};

export const createAssociatedListing = (ownListingValues, queryParams) => {
  let body = { ownListingValues, queryParams };
  return post('/api/teams/listing/create', body);
};

export const updateAssociatedListing = (ownListingUpdateValues, queryParams) => {
  let body = { ownListingUpdateValues, queryParams };
  return post('/api/teams/listing/update', body);
};

export const getAssociatedListing = body => {
  return post('/api/teams/listing/show', body);
};

export const getTeamAssociatedListing = body => {
  return post('/api/teams/listing/query', body);
};

export const updateListingStock = (payload, params) => {
  let body = { payload, params };
  return post('/api/teams/listing/stock', body);
};

export const callToDeleteListing = body => {
  return post('/api/teams/listing/delete', body);
};

export const fetchApiTeamTransactions = body => {
  return post('/api/teams/transactions/fetch', body);
};

export const fetchApiTeamTransaction = (payload, params) => {
  let body = { payload, params };
  return post('/api/teams/transaction/fetch', body);
};

export const geocodeDealerAddresses = body => {
  return post('/api/dealers/geocode-lazy', body);
};

export const geocodeDealerAddress = body => {
  return post('/api/dealers/geocode-address', body);
};

export const geocodeDealerZip = body => {
  return post('/api/dealers/geocode-zip', body);
};

export const callToDeclineOrder = body => {
  return post('/api/teams/transaction/decline', body);
};

export const callToAcceptOrder = body => {
  return post('/api/teams/transaction/accept', body);
};

export const callToRejectOffer = body => {
  return post('/api/teams/transaction/reject', body);
};

export const callToApproveOffer = body => {
  return post('/api/teams/transaction/approve', body);
};

export const callToCounterOffer = body => {
  return post('/api/teams/transaction/counterOffer', body);
};

const fetchApiStoresWithOptions = (body = {}) => {
  return post('/api/stores/list', body);
};

export const fetchApiStoresLean = () => {
  return fetchApiStoresWithOptions({
    includeReviews: false,
    includeListingStats: false,
  });
};

export const fetchApiStoresWithReviews = () => {
  return fetchApiStoresWithOptions({
    includeReviews: true,
    includeListingStats: false,
  });
};

export const fetchApiStoresEnriched = (body = {}) => {
  return fetchApiStoresWithOptions({
    includeReviews: true,
    includeListingStats: true,
    ...body,
  });
};

// Deprecated compatibility alias. Prefer the explicit helpers above.
export const fetchApiStores = (body = {}) => {
  return fetchApiStoresWithOptions(body);
};

export const callToFetchStores = (body = {}) => {
  return post('/api/stores/fetch', body);
};

export const fetchApiCreators = body => {
  return post('/api/stores/creators', body);
};

export const callToPublishManufacturerListing = body => {
  return post('/api/manufacturers/publish', body);
};

export const callToUpdateManufacturerListing = body => {
  return post('/api/manufacturers/update', body);
};

export const callToFixStock = body => {
  return post('/api/manufacturers/stock/update', body);
};

export const callToAutoReplicateListings = body => {
  return post('/api/manufacturers/auto-replicate', body);
};

export const callToManualReplicateTrigger = body => {
  return post('/api/manufacturers/manual-replicate', body);
};

export const callToSetListingStatus = body => {
  return post('/api/transactions/handleStock', body);
};

export const fetchApiUsers = body => {
  return post('/api/getUsersData', body);
};

export const callToReviewAssociated = body => {
  return post('/api/teams/creators/review', body);
};

export const storeMessage = body => {
  return post('/api/teams/transaction/message', body);
};

export const callToFetchMessages = body => {
  return post('/api/teams/transaction/fetchMessages', body);
};

export const callToTransactionNotify = body => {
  return post('/api/transactions/notify', body);
};

export const callToSingleMessage = body => {
  return post('/api/messages/single', body);
};

export const callToMessageNotification = body => {
  return post('/api/transactions/notifyMessage', body);
};

export const callToNotifyNewProvider = body => {
  return post('/api/notifications/newProvider', body);
};

export const uploadFile = (path, file) => {
  const formData = new FormData();
  formData.append('file', file);

  return fetch(`${apiBaseUrl()}/api/awsbucket/upload/${path}`, {
    method: 'POST',
    body: formData,
  })
    .then(response => {
      if (!response.ok) {
        return response.json().then(error => {
          throw error;
        });
      }
      return response.json();
    })
    .catch(error => {
      throw error;
    });
};

export const removeFile = body => {
  return post('/api/awsbucket/remove', body);
};

export const generatePreSignedUrl = body => {
  return post('/api/awsbucket/generate-presigned-url', body);
};

export const sendInquiryAlert = body => {
  return post('/api/notifications/offer', body);
};

export const sendContactRequest = body => {
  return post('/api/contact', body, {
    headers: { 'Content-Type': 'application/json' },
  });
};

export const fetchHistoricalPrices = body => {
  return post('/api/historical-prices', body);
};
// Search V2 catalog search: POST the canonical request as JSON. A failed search rejects with the
// server's structured body, so callers read `error.error.code` (e.g. `cursor_expired`).
//
// See `server/api/search.js`.
export const searchListingsV2 = body => {
  return post('/api/search', body, {
    headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
  });
};

// Search V2 autocomplete for `/s`: at most 8 declarative suggestions. `signal` aborts a superseded
// request.
//
// See `server/api/search-suggest.js`.
export const suggestSearchV2 = (body, { signal } = {}) => {
  return post('/api/search/suggest', body, {
    headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
    signal,
  });
};

// `/s` AI query understanding: a manual natural-language submit → a validated interpretation or
// `fallback`. `signal` aborts a superseded or overdue request.
//
// See `server/api/search-interpret.js`.
export const interpretSearchV2 = (body, { signal } = {}) => {
  return post('/api/search/interpret', body, {
    headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
    signal,
  });
};

export const fetchStaffPicks = (params = {}) => {
  const queryString = Object.entries(params)
    .filter(([, value]) => value != null && value !== '')
    .map(([key, value]) => `${encodeURIComponent(key)}=${encodeURIComponent(value)}`)
    .join('&');

  return request(`/api/staff-picks${queryString ? `?${queryString}` : ''}`, {
    method: methods.GET,
  });
};

// Fetch commission configuration from Sharetribe Console
export const fetchCommission = () => {
  return request('/api/commission', {
    method: methods.GET,
    headers: { 'Content-Type': 'application/json' },
  });
};

// Export post method for use in other modules
export { post };

// Affirm payment functions
export const calculateAffirmAmount = body => {
  return post('/api/calculate-affirm-amount', body);
};

export const createAffirmPaymentIntent = body => {
  return post('/api/create-affirm-payment-intent', body);
};

export const createAffirmTransaction = body => {
  return post('/api/create-affirm-transaction', body);
};

// Fetch dealers for indie seller
export const fetchDealers = (body = {}) => {
  return post('/api/dealers/list', body);
};

// Multi-party payment function
export const processMultiPartyPayment = body => {
  return post('/api/multi-party-payment', body);
};
